import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import StatCard from '../components/StatCard'
import ProgressBar from '../components/ProgressBar'
import StatusBadge from '../components/StatusBadge'
import { api } from '../lib/api'
import type { Stats, Lecture } from '../lib/types'

export default function Dashboard() {
  const [stats, setStats] = useState<Stats>({ total_lectures: 0, manufactured: 0, completed: 0, pending: 0, total_cost: 0, progress_percent: 0 })
  const [recentLectures, setRecentLectures] = useState<Partial<Lecture>[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    async function load() {
      try {
        const [s, lectures] = await Promise.all([
          api.getStats(),
          api.getLectures(),
        ])
        setStats(s)
        setRecentLectures(lectures.slice(0, 5))
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) return <div className="text-center text-gray-400 py-12">جاري التحميل...</div>
  if (error) return <div className="text-center text-red-400 py-12">خطأ: {error}</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-vertex-gold">لوحة التحكم</h2>
        <Link
          to="/playlists"
          className="bg-vertex-gold text-vertex-deep px-4 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light transition-colors"
        >
          + استيراد قائمة تشغيل
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="إجمالي المحاضرات" value={stats.total_lectures} icon="🎓" />
        <StatCard label="تم تصنيعها" value={stats.manufactured} icon="⚙️" color="text-purple-400" />
        <StatCard label="مكتملة" value={stats.completed} icon="✅" color="text-green-400" />
        <StatCard label="التكلفة" value={`$${stats.total_cost.toFixed(2)}`} icon="💰" color="text-yellow-400" />
      </div>

      {/* Progress */}
      <div className="bg-vertex-navy rounded-lg p-4 border border-vertex-surface mb-8">
        <ProgressBar value={stats.progress_percent} label="التقدم الإجمالي" />
      </div>

      {/* Recent Lectures */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface">
        <div className="p-4 border-b border-vertex-surface">
          <h3 className="font-bold text-sm">آخر المحاضرات</h3>
        </div>
        {recentLectures.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            لا توجد محاضرات بعد.{' '}
            <Link to="/playlists" className="text-vertex-gold hover:underline">استورد أول قائمة تشغيل</Link>
          </div>
        ) : (
          <div className="divide-y divide-vertex-surface">
            {recentLectures.map((lec) => (
              <Link
                key={lec.id}
                to={`/lectures/${lec.id}`}
                className="flex items-center justify-between p-4 hover:bg-vertex-surface/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-vertex-gold font-mono text-sm">L{lec.lecture_number}</span>
                  <span className="text-sm">{lec.title_ar}</span>
                  <span className="text-xs text-gray-500">{lec.course_name}</span>
                </div>
                <StatusBadge status={lec.status || 'raw'} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
